import { ActionHeader, StyledGridCol, StyledGridRow } from "@/common/styles";
import Button from "@/common/elements/Button";
import { useEffect, useRef, useState } from "react";
import Modal from "@/common/elements/Modal";
import Table from "@/common/elements/Table";
import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import Select from "@/common/elements/InputElements/Select";
import TextField from "@/common/elements/InputElements/TextField";
import CommonDatePicker from "@/common/elements/DatePicker";
import {
  getOptionsFromRefList,
  hasCheckSecurity,
  isNonEmptyVariable,
  convertApiFields,
  getSQLTypeToHTMLType,
} from "@/util/common_function";
import { Form, Popconfirm } from "antd";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { setBookings, setDeleteAPis } from "@/slices/bookingsSlice";
import { v4 as uuidv4 } from "uuid";
import { set } from "lodash";
import API_ENDPOINTS from "@/util/api";
import { Entity_types, Security_Types } from "@/util/permission_bits";
import { LABELS_CAPITAL } from "@/util/usePermissionMask";
import {
  MODULES_LIST_WITH_ID,
  SCHEMA_NAME,
  SUB_MODULES_LIST_WITH_ID,
} from "@/util/common_variable";
import { FieldSecurityProvider } from "@/contexts/FieldSecurityContext";
import SecureFormItem from "@/common/SecureFormItem";
import useGridColumnConfig from "@/hooks/useGridColumnConfig";
import DynamicForm from "@/common/DynamicForm";
import { BookingsSectionTitle } from "./styles";
import api from "@/services/axios_custom";
import { fetchExtraTableRecord } from "@/util/extraColumnActions";

const APPOINTMENT_LOG_HEADERS = [
  { key: "dateSeenBy", text: "Date Seen" },
  { key: "timeSeenBy", text: "Time Seen" },
  { key: "appointmentOfficerStaff", text: "Appointment Officer" },
  { key: "reasonForAppt", text: "Reason for Appointment" },
  { key: "typeOfAppointment", text: "Type of Appointment" },
  { key: "notes", text: "Notes" },
];

const AppointmentLogDetails = (startingPoint = "") => {
  const [appointmentLogForm] = Form.useForm();
  const currentUser = JSON.parse(localStorage.getItem("userDetails"));
  const dispatch = useDispatch();

  const { bookings, bookingsFieldsEnabled } = useSelector(
    (state) => state?.bookings
  );
  const [isRowSelected, setIsRowSelected] = useState(false);
  const [tempTableList, setTempTableList] = useState([]);
  const [appoinmentLogId, setAppoinmentLogId] = useState(null);
  const [selectedRow, setSelectedRow] = useState();
  const [showAppointmentLog, setShowAppointmentLog] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);

  // -----------------------------------------------------------------------
  // AppointmentLogExtra — PCI's per-client dynamic "_U" columns for this
  // appointment (AppointmentLogInterface.asp's "Additional Appointment Log
  // Information" section, shown right after Notes).
  // -----------------------------------------------------------------------
  const [appointmentLogExtraFields, setAppointmentLogExtraFields] = useState([]);
  const [appointmentLogExtraFieldsReady, setAppointmentLogExtraFieldsReady] =
    useState(false);
  const appointmentLogExtraTypeMapRef = useRef({});
  const appointmentLogExtraIdRef = useRef(0);

  useEffect(() => {
    api
      .get(`${API_ENDPOINTS.getTableColumns}/AppointmentLogExtra`)
      .then((response) => {
        const tableData = (response?.data || []).filter((d) =>
          d?.columnName?.endsWith("_U")
        );
        const converted = convertApiFields(tableData, "AppointmentLogExtra");
        const typeMap = {};
        converted.forEach((c) => {
          typeMap[c.columnName] = getSQLTypeToHTMLType(c?.type);
        });
        appointmentLogExtraTypeMapRef.current = typeMap;
        setAppointmentLogExtraFields(converted);
        setAppointmentLogExtraFieldsReady(true);
      })
      .catch((error) => {
        console.error(
          "[AppointmentLogDetails] AppointmentLogExtra field defs fetch failed:",
          error
        );
      });
  }, []);

  // Fetches the saved AppointmentLogExtra row when editing an existing
  // appointment — a brand new appointment has no appointmentLogID yet.
  useEffect(() => {
    if (!appointmentLogExtraFieldsReady) return;
    const id = selectedRow?.appointmentLogID;
    if (!isNonEmptyVariable(id)) {
      appointmentLogExtraIdRef.current = 0;
      return;
    }

    fetchExtraTableRecord("AppointmentLogExtra", "appointmentLogID", id)
      .then((result) => {
        if (!result) return;
        appointmentLogExtraIdRef.current =
          result.raw?.AppointmentLogExtraID ?? 0;
        const withDates = Object.keys(result.values).reduce((acc, key) => {
          const val = result.values[key];
          const htmlType = appointmentLogExtraTypeMapRef.current[key];
          const isDateField = htmlType === "date" || htmlType === "datetime";
          acc[key] = isDateField && isNonEmptyVariable(val) ? dayjs(val) : val;
          return acc;
        }, {});
        appointmentLogForm.setFieldsValue(withDates);
      })
      .catch((error) => {
        console.error(
          "[AppointmentLogDetails] AppointmentLogExtra fetch failed:",
          error
        );
      });
  }, [selectedRow?.appointmentLogID, appointmentLogExtraFieldsReady]);

  const clickAppointmentLog = (val) => {
    setShowAppointmentLog(true);
    if (val == "edit") {
      setIsEdit(true);
    }
  };

  // "Config" — drag-reorder/resize columns, persisted per-user (see
  // docs/plans/grid-column-config-feature.md).
  const {
    headers: appointmentLogHeaders,
    isConfiguring,
    configButtonLabel,
    startConfiguring,
    confirmSave,
    discardChanges,
    dataTableRef,
  } = useGridColumnConfig("Bookings:AppointmentLog", APPOINTMENT_LOG_HEADERS);

  useEffect(() => {
    if (bookings?.appointmentLog?.length > 0) {
      setTempTableList(
        bookings?.appointmentLog.map((item) => {
          console.log(
            item,
            getOptionsFromRefList("", true).filter(
              (staff) => staff.value === item.appointmentOfficerStaffID
            )
          );
          return {
            ...item,
            appointmentOfficerStaff: getOptionsFromRefList("", true).filter(
              (staff) =>
                staff.value === parseInt(item.appointmentOfficerStaffID)
            )?.[0]?.label,
          };
        })
      );
    } else {
      setTempTableList([]);
    }
  }, [bookings]);

  useEffect(() => {
    canActivateHide();
  }, [bookingsFieldsEnabled]);

  useEffect(() => {
    appointmentLogForm.setFieldsValue(selectedRow);
  }, [selectedRow]);

  const canActivateHide = () => {
    setAppoinmentLogId(null);
    setIsRowSelected(false);
    setSelectedRow(null);
    setShowAppointmentLog(false);
    setIsEdit(false);
  };

  const patchSelectedRow = (selectedRow) => {
    setSelectedRow({
      dateTimeSeenBy: isNonEmptyVariable(selectedRow?.dateSeenBy)
        ? dayjs(
            `${selectedRow?.dateSeenBy || ""} ${selectedRow?.timeSeenBy || ""}`
          )
        : null,
      appointmentOfficerStaffID: selectedRow?.appointmentOfficerStaffID,
      reasonForAppt: selectedRow?.reasonForAppt,
      typeOfAppointment: selectedRow?.typeOfAppointment,
      notes: selectedRow?.notes,
      // Only a real, previously-saved appointment has this — used below to
      // fetch/save AppointmentLogExtra. A locally-added-but-unsaved row only
      // has a client-generated uuid `id`, not this.
      appointmentLogID: selectedRow?.appointmentLogID,
    });
    setAppoinmentLogId(selectedRow?.appointmentLogID || selectedRow?.id);
    setIsRowSelected(true);
  };

  const saveAptLog = () => {
    appointmentLogForm
      .validateFields()
      .then((values) => {
        // Split AppointmentLogExtra dynamic fields out of the form values —
        // nested onto appointmentLogExtra[0] below rather than left flat on
        // values, where they'd otherwise leak into the main bookings save
        // payload. See pages/Bookings/index.jsx's strip-before-POST and
        // saveAppointmentLogExtra.
        const extraFieldKeys = appointmentLogExtraFields.map(
          (f) => f.columnName
        );
        const extraData = {};
        extraFieldKeys.forEach((k) => {
          const val = values?.[k];
          const htmlType = appointmentLogExtraTypeMapRef.current[k];
          extraData[k] = val
            ? htmlType === "date"
              ? dayjs(val).format("MM/DD/YYYY")
              : htmlType === "datetime"
                ? dayjs(val).format("MM/DD/YYYY HH:mm")
                : val
            : null;
          delete values[k];
        });

        values.dateTimeSeenBy = isNonEmptyVariable(values?.dateTimeSeenBy)
          ? dayjs(values?.dateTimeSeenBy).format("MM/DD/YYYY HH:mm")
          : null;

        values.dateSeenBy = values?.dateTimeSeenBy?.split(" ")[0];
        values.timeSeenBy = values?.dateTimeSeenBy?.split(" ")[1];

        delete values?.dateTimeSeenBy;
        if (appoinmentLogId) {
          let data = [...tempTableList];
          data = data?.map((p) => {
            if (
              p?.appointmentLogID === appoinmentLogId ||
              p?.id === appoinmentLogId
            ) {
              const existingExtra = p?.appointmentLogExtra?.[0] || {};
              return {
                ...p,
                ...values,
                appointmentLogExtra:
                  extraFieldKeys.length > 0
                    ? [
                        {
                          ...existingExtra,
                          ...extraData,
                          appointmentLogExtraID:
                            existingExtra?.appointmentLogExtraID ??
                            appointmentLogExtraIdRef.current ??
                            0,
                          appointmentLogID:
                            existingExtra?.appointmentLogID ??
                            p?.appointmentLogID ??
                            0,
                        },
                      ]
                    : p?.appointmentLogExtra,
              };
            }
            return p;
          });
          updateReleaseNotes(data);
        } else {
          const newData = {
            ...values,
            id: uuidv4(),
            appointmentLogExtra:
              extraFieldKeys.length > 0
                ? [{ ...extraData, appointmentLogExtraID: 0, appointmentLogID: 0 }]
                : undefined,
          };
          const data = [...tempTableList, newData];
          updateReleaseNotes(data);
        }
        setShowAppointmentLog(false);
      })
      .catch((err) => console.log(err));
  };

  const addLog = () => {
    // A stale selectedRow?.appointmentLogID from a previously-viewed row
    // would otherwise make the AppointmentLogExtra fetch effect above
    // populate this fresh Add form with the old row's extra values.
    setSelectedRow(null);
    appointmentLogExtraIdRef.current = 0;
    const extraFieldKeys = appointmentLogExtraFields.map((f) => f.columnName);
    appointmentLogForm.setFieldsValue({
      dateTimeSeenBy: new Date() ? dayjs(new Date()) : null,
      appointmentOfficerStaffID: currentUser.staffId,
      reasonForAppt: null,
      typeOfAppointment: null,
      notes: null,
      ...Object.fromEntries(extraFieldKeys.map((k) => [k, null])),
    });
    setAppoinmentLogId(null);
    setShowAppointmentLog(true);
  };

  const deleteAppointmentLog = () => {
    const tempArray = [...tempTableList];
    const deleteIndex =
      tempArray?.length > 1
        ? tempArray?.findIndex(
            (item) =>
              item?.appointmentLogID === appoinmentLogId ||
              item?.id === appoinmentLogId
          )
        : 0;
    tempArray.splice(deleteIndex, 1);
    updateReleaseNotes(tempArray);

    const url = API_ENDPOINTS.appoinmentLog + "/id/" + appoinmentLogId;
    dispatch(setDeleteAPis(url));
    setAppoinmentLogId(null);
    setIsRowSelected(false);
    setSelectedRow(null);
  };

  const updateReleaseNotes = (tb) => {
    dispatch(
      setBookings({
        appointmentLog: [...tb],
      })
    );
  };

  useEffect(() => {
    if (isEdit) {
      let isShow = hasCheckSecurity(
        Security_Types.functionality,
        Entity_types.subModule,
        LABELS_CAPITAL.MODIFY,
        MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
        SUB_MODULES_LIST_WITH_ID.Inmate_Medical
      );
      if (isShow) {
        setIsDisabled(false);
      } else {
        setIsDisabled(true);
      }
    } else {
      setIsDisabled(false);
    }
  }, [isEdit]);

  return (
    <>
      <StyledGridRow justify={"space-between"}>
        <StyledGridCol>
          <Button
            onClick={addLog}
            disabled={
              !bookingsFieldsEnabled ||
              isConfiguring ||
              !hasCheckSecurity(
                Security_Types.functionality,
                Entity_types.subModule,
                LABELS_CAPITAL.ADD,
                MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
                SUB_MODULES_LIST_WITH_ID.Inmate_Medical
              )
            }
          >
            ADD
          </Button>
        </StyledGridCol>
        <StyledGridCol>
          <Button
            style={{ marginRight: "10px" }}
            disabled={
              !isRowSelected ||
              tempTableList?.length === 0 ||
              isConfiguring ||
              !hasCheckSecurity(
                Security_Types.functionality,
                Entity_types.subModule,
                LABELS_CAPITAL.VIEW,
                MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
                SUB_MODULES_LIST_WITH_ID.Inmate_Medical
              )
            }
            onClick={() => clickAppointmentLog("edit")}
          >
            Details
          </Button>

          <Popconfirm
            title="Delete the Appointment Log"
            description="Are you sure to delete this Appointment log?"
            onConfirm={deleteAppointmentLog}
            onCancel={() => {}}
            okText="Yes"
            cancelText="No"
          >
            <Button
              color="primary"
              type="primary"
              style={{ marginLeft: "10px" }}
              disabled={
                !isRowSelected ||
                tempTableList?.length === 0 ||
                !bookingsFieldsEnabled ||
                isConfiguring ||
                !hasCheckSecurity(
                  Security_Types.functionality,
                  Entity_types.subModule,
                  LABELS_CAPITAL.DELETE,
                  MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
                  SUB_MODULES_LIST_WITH_ID.Inmate_Medical
                )
              }
            >
              Delete
            </Button>
          </Popconfirm>

          {/* Config — drag-reorder/resize columns */}
          {!isConfiguring ? (
            <Button
              style={{ marginLeft: "10px" }}
              htmlType="button"
              onClick={startConfiguring}
            >
              {configButtonLabel}
            </Button>
          ) : (
            <Popconfirm
              title="Do you want to save the current list view configuration?"
              onConfirm={confirmSave}
              onCancel={discardChanges}
              okText="Yes"
              cancelText="No"
            >
              <Button style={{ marginLeft: "10px" }} htmlType="button">
                {configButtonLabel}
              </Button>
            </Popconfirm>
          )}
        </StyledGridCol>
      </StyledGridRow>

      <Modal
        header={"Appointment Log"}
        show={showAppointmentLog}
        size="xl"
        onHide={canActivateHide}
        footerActions={[
          {
            key: "save",
            text: "Save",
            onClick: saveAptLog,
            disabled: isDisabled || !bookingsFieldsEnabled,
          },
        ]}
        footerCloseAction={() => setIsEdit(false)}
        footerCloseActionText={"Close"}
      >
        {/* FieldSecurityProvider wraps the entire modal Form */}
        <FieldSecurityProvider
          entity={SCHEMA_NAME.APPOINTMENT_LOG}
          startingPoint={startingPoint}
          bookingsFieldsEnabled={bookingsFieldsEnabled}
        >
          <Form form={appointmentLogForm}>
            <StyledGridRow gutter={16}>
              <StyledGridCol span={6}>
                <SecureFormItem
                  fieldName="dateTimeSeenBy"
                  formItemProps={{ name: "dateTimeSeenBy", label: "Date Seen" }}
                >
                  <CommonDatePicker
                    placeholder="MM/DD/YYYY HH:mm"
                    showTime={true}
                    format="MM/DD/YYYY hh:mm"
                  />
                </SecureFormItem>
              </StyledGridCol>
              <StyledGridCol span={4}>
                <SecureFormItem
                  fieldName="appointmentOfficerStaffID"
                  formItemProps={{ name: "appointmentOfficerStaffID", label: "Seen By" }}
                >
                  <Select
                    options={getOptionsFromRefList("", true)}
                    placeholder="Seen By"
                    showSearch={true}
                    variant="outlined"
                    onChange={(e) => {
                      setTimeout(() => document.activeElement.blur(), 0);
                    }}
                  />
                </SecureFormItem>
              </StyledGridCol>
            </StyledGridRow>
            <StyledGridRow gutter={16}>
              <StyledGridCol span={6}>
                <SecureFormItem
                  fieldName="reasonForAppt"
                  formItemProps={{ name: "reasonForAppt", label: "Reason for Appointment" }}
                >
                  <Select
                    options={getOptionsFromRefList("Reason_for_Appt")}
                    showSearch={true}
                    variant="outlined"
                  />
                </SecureFormItem>
              </StyledGridCol>
              <StyledGridCol span={6}>
                <SecureFormItem
                  fieldName="typeOfAppointment"
                  formItemProps={{ name: "typeOfAppointment", label: "Type of Appointment" }}
                >
                  <Select
                    options={getOptionsFromRefList("Type_of_Appointment")}
                    showSearch={true}
                    variant="outlined"
                  />
                </SecureFormItem>
              </StyledGridCol>
            </StyledGridRow>
            <StyledGridRow gutter={16}>
              <StyledGridCol span={12}>
                <SecureFormItem
                  fieldName="notes"
                  formItemProps={{ name: "notes", label: "Additional Notes" }}
                >
                  <TextField
                    inputType="textarea"
                    as="textarea"
                    id="description"
                    name="description"
                    variant="outlined"
                    rows="5"
                  />
                </SecureFormItem>
              </StyledGridCol>
            </StyledGridRow>

            {appointmentLogExtraFields.length > 0 && (
              <>
                <BookingsSectionTitle>
                  <span>Additional Appointment Log Information</span>
                </BookingsSectionTitle>
                <StyledGridRow gutter={16}>
                  {appointmentLogExtraFields.map((sf, index) => (
                    <DynamicForm
                      key={index}
                      formAttributes={sf}
                      form={appointmentLogForm}
                      bookingsFieldsEnabled={bookingsFieldsEnabled}
                    />
                  ))}
                </StyledGridRow>
              </>
            )}
          </Form>
        </FieldSecurityProvider>
      </Modal>

      {hasCheckSecurity(
        Security_Types.functionality,
        Entity_types.subModule,
        LABELS_CAPITAL.VIEW,
        MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
        SUB_MODULES_LIST_WITH_ID.Inmate_Medical
      ) && (
        <Table
          ref={dataTableRef}
          headers={appointmentLogHeaders}
          draggable={isConfiguring}
          data={tempTableList}
          style={{ marginTop: "15px" }}
          onRowClick={(data) => patchSelectedRow(data)}
          onRowDoubleClick={(value) => {
            patchSelectedRow(value);
            clickAppointmentLog("edit");
          }}
          selected={selectedRow === null}
        />
      )}
    </>
  );
};

export default AppointmentLogDetails;